<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes } = useMessageContext();

function extractUrlFromContent() {
  const text = content.value || '';
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const matches = text.match(urlRegex);
  return matches ? matches[0] : '';
}

function extractDomain(url) {
  if (!url) return '';
  try {
    const domain = new URL(url).hostname;
    return domain.replace('www.', '');
  } catch {
    return '';
  }
}

const linkData = computed(() => {
  const attrs = contentAttributes.value || {};

  return {
    url: attrs.url || attrs.link || extractUrlFromContent(),
    title: attrs.title || attrs.name || '',
    description: attrs.description || attrs.text || '',
    thumbnail:
      attrs.thumbnail ||
      attrs.image ||
      attrs.preview_url ||
      attrs.thumbnailUrl ||
      '',
    siteName:
      attrs.siteName || attrs.site_name || extractDomain(attrs.url || ''),
  };
});

const hasPreview = computed(() => {
  return linkData.value.url || linkData.value.title;
});

const hasThumbnail = computed(() => {
  return !!linkData.value.thumbnail;
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden min-w-48 max-w-sm"
    data-bubble-name="link-preview"
  >
    <div v-if="hasPreview" class="space-y-0">
      <!-- Thumbnail -->
      <div v-if="hasThumbnail" class="w-full h-40 bg-n-alpha-2 overflow-hidden">
        <img
          :src="linkData.thumbnail"
          :alt="linkData.title || 'Link preview'"
          class="w-full h-full object-cover"
          loading="lazy"
          @error="$event.target.style.display = 'none'"
        />
      </div>

      <!-- Content -->
      <div class="p-3 space-y-1">
        <!-- Site name -->
        <div
          v-if="linkData.siteName"
          class="text-xs text-n-slate-11 uppercase tracking-wide"
        >
          {{ linkData.siteName }}
        </div>

        <!-- Title -->
        <div
          v-if="linkData.title"
          class="text-sm font-medium text-n-slate-12 line-clamp-2"
        >
          {{ linkData.title }}
        </div>

        <!-- Description -->
        <div
          v-if="linkData.description"
          class="text-sm text-n-slate-11 line-clamp-2"
        >
          {{ linkData.description }}
        </div>

        <!-- URL -->
        <a
          v-if="linkData.url"
          :href="linkData.url"
          target="_blank"
          rel="noopener noreferrer nofollow"
          class="flex items-center gap-1 text-xs text-woot-600 hover:underline mt-2"
        >
          <Icon icon="i-ph-link" class="size-3" />
          <span class="truncate">{{ linkData.url }}</span>
        </a>
      </div>
    </div>

    <!-- Fallback for just URL -->
    <div v-else class="p-3">
      <a
        v-if="content"
        :href="extractUrlFromContent()"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="flex items-center gap-2 text-sm text-woot-600 hover:underline"
      >
        <Icon icon="i-ph-link" class="size-4" />
        <span class="truncate">{{ content }}</span>
      </a>
    </div>
  </BaseBubble>
</template>
